import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    company_name: "",
    company_currency: "FCFA",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Bienvenue ! Votre agence est prête.");
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur d'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-slate-50 dark:bg-slate-950">
      <div className="relative hidden md:block">
        <img src="https://images.pexels.com/photos/31293033/pexels-photo-31293033.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/50 via-slate-950/60 to-slate-950/80" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets-0z36b82j.emergentagent.net/job_ticketing-transport/artifacts/5p6cedk3_1000607872.png" alt="Transport.pro" className="w-12 h-12 rounded-xl object-cover" />
            <span className="font-display font-bold text-xl">Transport.pro</span>
          </Link>
          <div>
            <h1 className="font-display text-4xl font-bold leading-tight max-w-md">Lancez votre agence en 2 minutes</h1>
            <p className="mt-4 text-white/80 max-w-md">Plan FREE inclus. Ajoutez vos trajets, vendez vos billets, encaissez vos clients.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader>
              <CardTitle className="font-display text-2xl">Créer mon agence</CardTitle>
              <p className="text-sm text-slate-500">Vous devenez Créateur de votre entreprise</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-3" data-testid="register-form">
                <div>
                  <Label>Nom complet</Label>
                  <Input required value={form.full_name} onChange={set("full_name")} data-testid="reg-name-input" placeholder="Ex: Boris Keita" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input required type="email" value={form.email} onChange={set("email")} data-testid="reg-email-input" placeholder="vous@exemple.com" />
                </div>
                <div>
                  <Label>Mot de passe</Label>
                  <Input required type="password" value={form.password} onChange={set("password")} data-testid="reg-password-input" placeholder="Minimum 6 caractères" />
                </div>
                <div>
                  <Label>Nom de l'agence</Label>
                  <Input required value={form.company_name} onChange={set("company_name")} data-testid="reg-company-input" placeholder="Ex: Sotra Transport" />
                </div>
                <div>
                  <Label>Devise</Label>
                  <Select value={form.company_currency} onValueChange={(v) => setForm((f) => ({ ...f, company_currency: v }))}>
                    <SelectTrigger data-testid="reg-currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FCFA">FCFA (BCEAO / BEAC)</SelectItem>
                      <SelectItem value="€">Euro €</SelectItem>
                      <SelectItem value="$">Dollar $</SelectItem>
                      <SelectItem value="£">Livre £</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={loading} data-testid="register-submit-btn"
                  className="w-full btn-pill bg-orange-500 hover:bg-orange-600 h-11 mt-2">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Création…</> : "Créer mon compte"}
                </Button>
                <div className="text-sm text-center text-slate-500">
                  Déjà un compte ? <Link to="/login" className="text-orange-500 hover:underline font-medium" data-testid="link-to-login">Se connecter</Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
